import React, { useState, useEffect } from 'react';
import { getApiErrorMessage, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import contractService, { Contract, DocumentStatus, DocumentTrackingItemForFrontend } from '../../services/contractService';
import companyService from '../../services/companyService';
import { showAlert } from '../../utils/alert';
import { RiFileTextLine, RiSendPlaneLine, RiCheckDoubleLine, RiTimeLine, RiEyeLine, RiMailLine, RiRefreshLine, RiFlowChart, RiFileAddLine, RiFileDownloadLine } from 'react-icons/ri';
import Layout from '../../components/ui/Layout';
import DocumentStatusModal from '../../components/contract/DocumentStatusModal';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/ui/SearchableSelect';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import DualScrollWrapper from '../../components/ui/DualScrollWrapper';
import { usePermission } from '../../hooks/usePermission';
import { CanEdit } from '../../components/auth/PermissionGuard';
import { getLocalDateString } from '../../utils/dateUtils'; // KD3 S2365: วันที่เวลาท้องถิ่น (toISOString = UTC → ตี 0-7 ได้วันเมื่อวาน)

/**
 * BUG-S2077-001: สัญญาที่ยกเลิก/หมดอายุแล้ว backend ปฏิเสธการเดินเอกสารทุกขั้น (400) แต่หน้านี้เคยโชว์ปุ่ม
 * ขั้นถัดไปตาม document_status อย่างเดียว → ผู้ใช้กดแล้วได้ error ทุกครั้ง
 */
const isContractEnded = (c: { contract_status?: string | null }) =>
  c.contract_status === 'TERMINATED' || c.contract_status === 'EXPIRED';

const ContractDocumentTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { canView, canEdit, canExport } = usePermission();
  const [contracts, setContracts] = useState<DocumentTrackingItemForFrontend[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [companies, setCompanies] = useState<Array<{id: number; name: string}>>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<{[key: string]: number}>({});
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    action: 'generate' | 'send' | 'sign' | 'complete';
    contractId: number;
    trackingId: number;  // เพิ่ม trackingId
    contractNumber: string;
  } | null>(null);

  // รับ contract_id จาก URL params
  const contractIdFromUrl = searchParams.get('contract_id');

  // ตรวจสอบ authentication
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // ตรวจสอบ permission
  useEffect(() => {
    if (!canView('contracts')) {
      showAlert('ไม่มีสิทธิ์เข้าถึง', 'คุณไม่มีสิทธิ์ในการดูการติดตามเอกสารสัญญา', 'error');
      navigate('/contracts');
    }
  }, [canView, navigate]);

  useEffect(() => {
    if (user) {
      loadCompanies();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadContracts();
    }
  }, [statusFilter, searchTerm, companyFilter, page, contractIdFromUrl, user]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getCompanies({ limit: 1000 });
      setCompanies(response.items.map(c => ({ id: c.id, name: c.name })));
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  // ตัวกรองชุดเดียวกันทั้งตารางและ Export (BUG-S2077-003 — export ต้องเท่ากับที่ผู้ใช้กรองไว้ ไม่ใช่แค่หน้าที่เห็น)
  const buildTrackingParams = (overrides: { page: number; limit: number }) => {
    const params: any = { ...overrides };

    // Filter by status
    if (statusFilter !== 'ALL') {
      params.document_status = statusFilter;
    }

    // Search by contract number or company name
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    // Filter by company
    if (companyFilter) {
      params.company_id = parseInt(companyFilter);
    }

    // Filter by specific contract if came from contract page
    if (contractIdFromUrl) {
      params.contract_id = parseInt(contractIdFromUrl);
    }
    return params;
  };

  const loadContracts = async () => {
    try {
      setLoading(true);
      const params = buildTrackingParams({ page, limit: 20 });

      const response = await contractService.getDocumentTracking(params);

      if (response && response.items) {
        setContracts(response.items);
        setTotal(response.total);
        setTotalPages(Math.ceil(response.total / 20));
      } else {
        console.error('Invalid response format:', response);
        setContracts([]);
        setTotal(0);
        setTotalPages(0);
      }

      // สถิติ: ใช้ aggregate stats จาก backend (นับจากข้อมูลทุกหน้าที่ตรง filter ไม่ใช่เฉพาะหน้าปัจจุบัน)
      // fallback นับจากหน้าปัจจุบันถ้า backend รุ่นเก่ายังไม่ส่ง stats (กัน deploy skew)
      if (response && response.stats) {
        setStats(response.stats);
      } else if (response && response.items && Array.isArray(response.items)) {
        const statusCounts: {[key: string]: number} = {};
        response.items.forEach(contract => {
          const status = contract.document_status || DocumentStatus.PENDING;
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        setStats(statusCounts);
      } else {
        setStats({});
      }
    } catch (error) {
      console.error('Error loading contracts:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Refresh handler
  const handleRefresh = () => {
    loadContracts();
  };

  // Export Excel handler
  const handleExportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      // BUG-S2077-003: เดิม export จาก state `contracts` = เฉพาะหน้าที่เห็น (20 จาก 44) · ดึงครบทุกหน้าตามตัวกรองเดียวกัน
      const EXPORT_PAGE_SIZE = 100; // backend cap le=100
      const allRows: typeof contracts = [];
      for (let p = 1; ; p++) {
        const res = await contractService.getDocumentTracking(buildTrackingParams({ page: p, limit: EXPORT_PAGE_SIZE }));
        const items = res?.items || [];
        allRows.push(...items);
        if (items.length < EXPORT_PAGE_SIZE || allRows.length >= (res?.total ?? 0) || p >= 100) break;
      }
      const wsData = [
        ['เลขที่สัญญา', 'บริษัท', 'สถานะเอกสาร', 'ระยะเวลา (วัน)'],
        ...allRows.map(contract => [
          contract.contract_number,
          contract.company_name || contract.company?.name || '-',
          getStatusText(contract.document_status || DocumentStatus.PENDING),
          contract.days_in_current_status
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'DocumentTracking');
      XLSX.writeFile(wb, `document_tracking_${getLocalDateString()}.xlsx`);

      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    }
  };

  // Filter change handler
  const handleFilterChange = (key: string, value: any) => {
    if (key === 'company') {
      setCompanyFilter(value);
    } else if (key === 'status') {
      setStatusFilter(value);
    }
    setPage(1);
  };

  // Clear all filters handler
  const handleClearAll = () => {
    setStatusFilter('ALL');
    setCompanyFilter('');
    setSearchTerm('');
    setPage(1);
  };

  // Define filters configuration
  const filters: FilterConfig[] = [
    {
      key: 'company',
      label: 'บริษัท',
      type: 'searchable-select',
      options: companies.map(c => ({ value: c.id.toString(), label: c.name })),
      placeholder: 'เลือกบริษัท',
      tagColor: 'primary',
    },
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: DocumentStatus.PENDING, label: 'รอดำเนินการ' },
        { value: DocumentStatus.DRAFT, label: 'ฉบับร่าง' },
        { value: DocumentStatus.GENERATED, label: 'สร้าง PDF แล้ว' },
        { value: DocumentStatus.SENT, label: 'ส่งให้ลูกค้าแล้ว' },
        { value: DocumentStatus.CUSTOMER_SIGNED, label: 'ลูกค้าเซ็นแล้ว' },
        { value: DocumentStatus.COMPLETED, label: 'เสร็จสมบูรณ์' },
      ],
      placeholder: 'ทั้งหมด',
      tagColor: 'success',
    },
  ];

  const openModal = (action: 'generate' | 'send' | 'sign' | 'complete', trackingId: number, contractId: number, contractNumber: string) => {
    setModalConfig({
      isOpen: true,
      action,
      contractId,
      trackingId,
      contractNumber
    });
  };

  const closeModal = () => {
    setModalConfig(null);
  };

  const handleModalConfirm = async (data: any) => {
    if (!modalConfig) return;

    try {
      switch (modalConfig.action) {
        case 'generate':
          await contractService.generateDocument(modalConfig.contractId, modalConfig.trackingId, data);
          showAlert('สำเร็จ', 'สร้างเอกสาร PDF เรียบร้อยแล้ว', 'success');
          break;
        case 'send':
          await contractService.sendDocument(modalConfig.contractId, data, modalConfig.trackingId);
          showAlert('สำเร็จ', 'บันทึกการส่งเอกสารเรียบร้อยแล้ว', 'success');
          break;
        case 'sign':
          await contractService.markDocumentSigned(modalConfig.contractId, data, modalConfig.trackingId);
          showAlert('สำเร็จ', 'บันทึกการเซ็นเอกสารเรียบร้อยแล้ว', 'success');
          break;
        case 'complete':
          await contractService.completeDocument(modalConfig.contractId, data, modalConfig.trackingId);
          showAlert('สำเร็จ', 'บันทึกว่าเอกสารเสร็จสมบูรณ์แล้ว', 'success');
          break;
      }
      loadContracts();
      closeModal();
    } catch (error: any) {
      console.error(`Error ${modalConfig.action}:`, error);
      // BUG-S172-DOCSIGNMSG (ตระกูลเดียวกับ BUG-NOTI-DEADREF-RAWERR / BUG-S170-STATUSMSG):
      // backend บอกสาเหตุที่ทำต่อไม่ได้ไว้ชัดแล้ว เช่น
      // "ไม่สามารถบันทึกการเซ็นสัญญาได้ — สัญญานี้ยังไม่มีผู้ลงนาม กรุณาเพิ่มผู้ลงนามก่อน"
      // แต่หน้านี้ทิ้งทั้งหมดแล้วขึ้น "ไม่สามารถดำเนินการได้" ⇒ ผู้ใช้ตันโดยไม่รู้ว่าต้องไปเพิ่มผู้ลงนาม
      // BUG-S2084-002: ตัวกลางเดียวกับทั้งโมดูล (รวมกรณีเน็ตหลุด/timeout)
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'ไม่สามารถดำเนินการได้'), 'error');
      // BUG-S2090-001: สถานะเอกสารอาจถูกเลื่อนจากที่อื่น (อีกแท็บ/อีกคน) → โหลดตารางใหม่ให้เห็นสถานะจริง
      loadContracts();
    }
  };

  const getStatusColor = (status: DocumentStatus): string => {
    switch (status) {
      case DocumentStatus.PENDING:
        return 'bg-secondary-100 text-secondary-800';
      case DocumentStatus.DRAFT:
        return 'bg-secondary-100 text-secondary-800';
      case DocumentStatus.GENERATED:
        return 'bg-info-100 text-info-800';
      case DocumentStatus.SENT:
        return 'bg-warning-100 text-warning-800';
      case DocumentStatus.CUSTOMER_SIGNED:
        return 'bg-success-100 text-success-800';
      case DocumentStatus.COMPLETED:
        // FINDING-S719-001 FIX: bg-success-600 + text-white = 3.74 contrast (fails WCAG 2 AA 4.5:1)
        // bg-success-700 (#0f766e) + text-white = ~4.88 contrast (passes)
        return 'bg-success-700 text-white';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  const getStatusText = (status: DocumentStatus): string => {
    switch (status) {
      case DocumentStatus.PENDING:
        return 'รอดำเนินการ';
      case DocumentStatus.DRAFT:
        return 'ฉบับร่าง';
      case DocumentStatus.GENERATED:
        return 'สร้าง PDF แล้ว';
      case DocumentStatus.SENT:
        return 'ส่งให้ลูกค้าแล้ว';
      case DocumentStatus.CUSTOMER_SIGNED:
        return 'ลูกค้าเซ็นแล้ว';
      case DocumentStatus.COMPLETED:
        return 'เสร็จสมบูรณ์';
      default:
        return status || 'ไม่ระบุ';
    }
  };

  const getDaysColor = (days: number): string => {
    if (days > 7) return 'text-danger-600';
    if (days > 3) return 'text-warning-600';
    return 'text-secondary-600';
  };

  return (
    <Layout>
      {/* Executive Navy Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiFileTextLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ติดตามสถานะเอกสารสัญญา</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการและติดตามการส่งและเซ็นเอกสาร</p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-secondary-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-secondary-700">{stats[DocumentStatus.PENDING] || 0}</div>
          <div className="text-sm text-secondary-700">รอดำเนินการ</div>
        </div>
        <div className="bg-info-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-info-700">{stats[DocumentStatus.GENERATED] || 0}</div>
          <div className="text-sm text-info-700">สร้าง PDF แล้ว</div>
        </div>
        <div className="bg-warning-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-warning-700">{stats[DocumentStatus.SENT] || 0}</div>
          <div className="text-sm text-warning-700">ส่งให้ลูกค้าแล้ว</div>
        </div>
        <div className="bg-success-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-success-700">{stats[DocumentStatus.CUSTOMER_SIGNED] || 0}</div>
          <div className="text-sm text-success-700">ลูกค้าเซ็นแล้ว</div>
        </div>
        <div className="bg-accent-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-accent-700">{stats[DocumentStatus.COMPLETED] || 0}</div>
          <div className="text-sm text-accent-700">เสร็จสมบูรณ์</div>
        </div>
        <div className="bg-info-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-info-700">{total}</div>
          <div className="text-sm text-info-700">รวมทั้งหมด</div>
        </div>
      </div>

      {/* Contract ID Filter Banner */}
      {contractIdFromUrl && (
        <div className="mb-4 p-3 bg-info-50 border border-info-200 rounded-md">
          <div className="flex items-center justify-between">
            <p className="text-sm text-info-700">
              กำลังแสดงเฉพาะเอกสารของสัญญาที่เลือก
            </p>
            <button
              onClick={() => navigate('/contracts/tracking')}
              className="text-sm text-info-600 hover:text-info-800"
            >
              ดูทั้งหมด
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons with Micro-interactions */}
      <div className="mb-5 flex items-center gap-2">
        <button
          onClick={handleRefresh}
          className="group inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-200 text-secondary-700 text-sm font-medium rounded-xl hover:bg-secondary-50 hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-sm transition-all duration-200"
        >
          <RiRefreshLine className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
          รีเฟรช
        </button>
        {canExport('contracts') && (
          <button
            onClick={handleExportExcel}
            disabled={!contracts?.length}
            className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[42px] py-2.5 bg-white border border-secondary-200 text-secondary-700 text-sm font-medium rounded-xl hover:bg-secondary-50 hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            <RiFileDownloadLine className="w-5 h-5 mr-2 text-success-600" />
            ส่งออก Excel
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <SearchFilterCard
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="ค้นหาเลขที่สัญญา, ชื่อบริษัท..."
        filters={filters}
        filterValues={{
          company: companyFilter,
          status: statusFilter === 'ALL' ? '' : statusFilter
        }}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
      />

      {/* Contract List */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info-600 mx-auto"></div>
            <p className="mt-2 text-secondary-600">กำลังโหลดข้อมูล...</p>
          </div>
        ) : (
          <>
            <DualScrollWrapper className="border border-secondary-200 rounded-lg">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50 border-b-2 border-secondary-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-600">
                      เลขที่สัญญา
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-600">
                      บริษัท
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-600">
                      สถานะเอกสาร
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-600">
                      ระยะเวลา
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-600">
                      การกระทำ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {contracts.map((contract) => (
                    <tr key={contract.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-secondary-900">
                              {contract.contract_number}
                            </div>
                            {contract.contract_type && (
                              <div className="flex items-center text-xs text-secondary-500">
                                <RiFlowChart className="w-3 h-3 mr-1" />
                                {contract.contract_type === 'MAIN' ? 'สัญญาหลัก' : 
                                 contract.contract_type === 'AMENDMENT' ? 'สัญญาเพิ่มเติม' : 
                                 contract.contract_type}
                              </div>
                            )}
                            {contract.master_contract_number && (
                              <div className="flex items-center text-xs text-info-600">
                                <RiFlowChart className="w-3 h-3 mr-1" />
                                สำหรับสัญญา: {contract.master_contract_number}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {contract.company_name || contract.company?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(contract.document_status || DocumentStatus.PENDING)}`}>
                          {getStatusText(contract.document_status || DocumentStatus.PENDING)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={getDaysColor(contract.days_in_current_status)}>
                          {contract.days_in_current_status} วัน
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/contracts/${contract.contract_id}`)}
                            className="text-info-600 hover:text-info-900"
                            title="ดูรายละเอียด"
                          >
                            <RiEyeLine className="w-4 h-4" />
                          </button>

                          {/* Permission Guard: ซ่อนปุ่มสร้าง PDF ถ้าไม่มีสิทธิ์ EDIT */}
                          <CanEdit module="contracts">
                            {!isContractEnded(contract) && contract.document_status === DocumentStatus.PENDING && (
                              <button
                                onClick={() => openModal('generate', contract.id, contract.contract_id, contract.contract_number)}
                                className="text-info-600 hover:text-info-900"
                                title="สร้างเอกสาร PDF"
                              >
                                <RiFileAddLine className="w-4 h-4" />
                              </button>
                            )}
                          </CanEdit>

                          {/* Permission Guard: ซ่อนปุ่มบันทึกว่าส่งแล้ว ถ้าไม่มีสิทธิ์ EDIT */}
                          <CanEdit module="contracts">
                            {!isContractEnded(contract) && contract.document_status === DocumentStatus.GENERATED && (
                              <button
                                onClick={() => openModal('send', contract.id, contract.contract_id, contract.contract_number)}
                                className="text-warning-600 hover:text-warning-900"
                                title="บันทึกว่าส่งแล้ว"
                              >
                                <RiSendPlaneLine className="w-4 h-4" />
                              </button>
                            )}
                          </CanEdit>

                          {/* Permission Guard: ซ่อนปุ่มบันทึกว่าเซ็นแล้ว ถ้าไม่มีสิทธิ์ EDIT */}
                          <CanEdit module="contracts">
                            {!isContractEnded(contract) && contract.document_status === DocumentStatus.SENT && (
                              <button
                                onClick={() => openModal('sign', contract.id, contract.contract_id, contract.contract_number)}
                                className="text-success-600 hover:text-success-900"
                                title="บันทึกว่าเซ็นแล้ว"
                              >
                                <RiCheckDoubleLine className="w-4 h-4" />
                              </button>
                            )}
                          </CanEdit>

                          {/* Permission Guard: ซ่อนปุ่มเสร็จสมบูรณ์ ถ้าไม่มีสิทธิ์ EDIT */}
                          <CanEdit module="contracts">
                            {!isContractEnded(contract) && contract.document_status === DocumentStatus.CUSTOMER_SIGNED && (
                              <button
                                onClick={() => openModal('complete', contract.id, contract.contract_id, contract.contract_number)}
                                className="text-accent-600 hover:text-accent-900"
                                title="เสร็จสมบูรณ์"
                              >
                                <RiFileTextLine className="w-4 h-4" />
                              </button>
                            )}
                          </CanEdit>

                          {isContractEnded(contract) && contract.document_status !== DocumentStatus.COMPLETED && (
                            <span className="text-xs text-secondary-400" title="สัญญานี้สิ้นสุดแล้ว เดินเอกสารต่อไม่ได้">
                              สัญญาสิ้นสุดแล้ว
                            </span>
                          )}

                          <button
                            onClick={() => navigate(`/contracts/${contract.contract_id}/logs`)}
                            className="text-secondary-600 hover:text-secondary-900"
                            title="ดูประวัติ"
                          >
                            <RiTimeLine className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DualScrollWrapper>

            {contracts.length === 0 && !loading && (
                <div className="text-center py-8">
                  <RiFileTextLine className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                  <p className="text-secondary-500">ไม่พบข้อมูลสัญญา</p>
                  {searchTerm && (
                    <p className="text-sm text-secondary-400 mt-2">
                      ลองค้นหาด้วยคำอื่นหรือเปลี่ยนฟิลเตอร์
                    </p>
                  )}
                </div>
              )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-secondary-50 px-4 py-3 flex items-center justify-between sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50"
                  >
                    ก่อนหน้า
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50"
                  >
                    ถัดไป
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => setPage(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === index + 1
                            ? 'z-10 bg-info-50 border-info-500 text-info-600'
                            : 'bg-white border-secondary-300 text-secondary-500 hover:bg-secondary-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modalConfig && (
        <DocumentStatusModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          onConfirm={handleModalConfirm}
          action={modalConfig.action}
          contractNumber={modalConfig.contractNumber}
        />
      )}
    </Layout>
  );
};

export default ContractDocumentTrackingPage;